import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import type {Question, CandidateProfile} from 'src/types/referral-external';
import {AnalyticsService} from 'src/analytics';
import {useHistory} from 'src/rerouter';
import {useSelector} from 'react-redux';

import classify from 'src/utils/classify';
import {
  QUESTIONNAIRE_TYPES,
  DEFAULT_TEXT_COLOR,
  noOp,
  CUSTOMER_TYPE,
  getReferralUrlSegment,
} from 'src/utils/referral-external';
import {selectUserAgent} from 'src/selectors/accounts';
import {isMobile} from 'src/utils/responsive';

import {
  BodyLarge,
  FormLabelSmall,
  SubTitleLarge,
  SubTitleMedium,
  Button,
  Checkbox,
  Card,
  Link,
} from '@spaced-out/ui-design-system/lib/components/index';

import {
  QuestionComponents,
  isQuestionVisible,
} from 'src/components/referral-external/candidate-form/form-questions';
import TrackFormModal from 'src/components/referral-external/track';
import css from './form.css';


type FormProps = {
  questions: Question[];
  answers?: Map<number, string | string[] | null | undefined>;
  brandName?: string;
  program_name?: string;
  welcome_message?: string;
  is_tracking_enabled?: boolean;
  track_referral_by_email?: boolean;
  customer_type?: string;
  onQuestionResponseChange?: (
    questionId: number,
    fieldType: string,
    value?: string | string[] | null | undefined,
  ) => unknown;
  onSubmit?: () => unknown;
  onCancel?: () => unknown;
  openTnc?: () => unknown;
  handleWitdhdraw?: () => unknown;
  referralLinkId?: string;
  errors?: Map<
    number,
    {
      message: string;
    }
  >;
  tncAccepted?: boolean;
  setTncAccepted?: (arg1: boolean) => unknown;
  tncError?: string | null | undefined;
  setTncError?: (arg1: string) => void;
  must_acknowledge_terms?: boolean;
  submitting?: boolean;
  token?: string | null;
  userProfileResponse?: CandidateProfile | null;
  brandButtonColor?: string;
  demoForm: boolean;
  isIframe?: boolean;
};

const Form = ({
  questions,
  answers = new Map(),
  brandName = '',
  brandButtonColor = '',
  program_name = '',
  welcome_message = '',
  is_tracking_enabled = true,
  track_referral_by_email = false,
  customer_type = '',
  onQuestionResponseChange = noOp,
  onSubmit = noOp,
  onCancel = noOp,
  openTnc = noOp,
  referralLinkId = '',
  errors = new Map(),
  tncAccepted = false,
  setTncAccepted = noOp,
  handleWitdhdraw = noOp,
  tncError = null,
  setTncError,
  must_acknowledge_terms = false,
  submitting = false,
  token = '',
  userProfileResponse,
  demoForm,
  isIframe = false,
}: FormProps) => {
  const {t} = useLabelTranslation();
  const router = useHistory();
  const userAgent = useSelector(selectUserAgent);
  const [showTrackModal, setShowTrackModal] = React.useState(false);

  const referralQuestions = questions.filter(
    (question) =>
      question.is_question_set &&
      question.display_section === 'referral' &&
      isQuestionVisible(token || '', question),
  );

  const referrerQuestions = questions.filter((question) => {
    const isQuestionSetAndVisible =
      question.is_question_set &&
      question.display_section === 'referrer' &&
      isQuestionVisible(token || '', question);

    if (!isQuestionSetAndVisible) {
      return false;
    }

    if (isIframe) {
      return !['referrer_name', 'referred_by'].includes(
        question.response_field,
      );
    }

    return true;
  });

  const onTrackReferrals = () => {
    if (track_referral_by_email && isMobile(userAgent)) {
      router.push({
        pathname: getReferralUrlSegment(referralLinkId),
        query: {action: 'track'},
      });
      return;
    }
    setShowTrackModal(true);
  };

  return (
    <div
      className={classify(css.container, {
        [css.formDemo]: demoForm,
      })}
    >
      <div className={css.header}>
        <SubTitleLarge className={css.headerText}>{program_name}</SubTitleLarge>
        {token ? (
          <div className={css.referralSideButtons}>
            <Button
              type="primary"
              onClick={() => handleWitdhdraw()}
              style={{
                backgroundColor: brandButtonColor,
                boxShadow: `0 0 0 2px ${brandButtonColor}`,
                color: DEFAULT_TEXT_COLOR,
              }}
            >
              {t('Withdraw', 'Withdraw')}
            </Button>
          </div>
        ) : (
          is_tracking_enabled &&
          // hide the track button if agency business type is corporate
          customer_type !== CUSTOMER_TYPE.CORPORATE && (
            <div className={css.referralSideButtons}>
              <Button
                type="secondary"
                onClick={onTrackReferrals}
                style={{
                  backgroundColor: brandButtonColor,
                  boxShadow: `0 0 0 2px ${brandButtonColor}`,
                  color: DEFAULT_TEXT_COLOR,
                }}
              >
                {t('Track_Referrals', 'Track Referrals')}
              </Button>
            </div>
          )
        )}
      </div>

      <div className={css.form}>
        <BodyLarge className={css.welcomeMessage}>
          {!token
            ? welcome_message
            : `${t('Hi', 'Hi')} ${
                userProfileResponse?.candidate_name || ''
              }! ${t(
                'You_have_been_referred_by',
                'You have been referred by',
              )} ${userProfileResponse?.referrer_name || ''}. ${t(
                'Your_profile_details_submitted_are_given_below',
                'Your profile details submitted are given below',
              )}:`}
        </BodyLarge>
        {!referralQuestions.length && !referrerQuestions.length ? (
          <div className={css.emptyContainer}>
            <SubTitleLarge>
              {t('Form_is_not_built_yet', 'Form is not built yet')}
            </SubTitleLarge>
          </div>
        ) : (
          <>
            <Card classNames={{wrapper: css.formCard}}>
              <SubTitleMedium className={css.cardHeader}>
                {token
                  ? t('Your_Details', 'Your Details')
                  : t('Referral_Details', 'Referral Details')}
              </SubTitleMedium>
              <div className={css.cardContent}>
                {referralQuestions.map((question) => {
                  const Component = QuestionComponents[question.type];
                  const value = answers.get(question.id) ?? null;

                  return (
                    <Component
                      key={question.id}
                      question={question}
                      value={value}
                      errors={errors}
                      onChange={onQuestionResponseChange}
                      token={token || ''}
                      referralLinkId={referralLinkId || ''}
                      title={userProfileResponse?.job_title}
                    />
                  );
                })}
              </div>
            </Card>
            {!token && referrerQuestions.length > 0 && (
              <Card classNames={{wrapper: css.formCard}}>
                <SubTitleMedium className={css.cardHeader}>
                  {t('Your_Details', 'Your Details')}
                </SubTitleMedium>
                <div className={css.cardContent}>
                  {referrerQuestions.map((question) => {
                    const Component = QuestionComponents[question.type];
                    const value = answers.get(question.id) ?? null;

                    return (
                      <Component
                        key={question.id}
                        question={question}
                        value={value}
                        errors={errors}
                        onChange={onQuestionResponseChange}
                        referralLinkId={referralLinkId}
                      />
                    );
                  })}
                  {!isIframe && (
                    <div style={{alignItems: 'center'}}>
                      <Checkbox
                        checked={tncAccepted}
                        onChange={(_) => {
                          setTncAccepted(!tncAccepted);
                          if (
                            setTncError &&
                            must_acknowledge_terms &&
                            !tncAccepted
                          ) {
                            setTncError('');
                          }
                        }}
                      />
                      <FormLabelSmall className={css.tncText} color="secondary">
                        {t(
                          'I_have_read_and_agree_to_the',
                          'I have read and agree to the',
                        )}
                        <Link className={css.tncLink} onClick={openTnc}>
                          {t('Terms_and_Conditions', 'Terms and Conditions')}
                        </Link>
                        {`${t('of', 'of')} `}
                        {brandName}
                        {` ${t('Referrals_Program', 'Referrals Program')}`}
                      </FormLabelSmall>
                    </div>
                  )}
                  <div>
                    {tncError && (
                      <FormLabelSmall color="danger">{tncError}</FormLabelSmall>
                    )}
                  </div>
                </div>
              </Card>
            )}
          </>
        )}
      </div>
      <div className={css.footer}>
        <Button
          type="tertiary"
          onClick={onCancel}
          style={{
            borderColor: brandButtonColor,
            boxShadow: `0 0 0 2px ${brandButtonColor}`,
            opacity: '0.7',
            color: DEFAULT_TEXT_COLOR,
            backgroundColor: brandButtonColor,
          }}
          classNames={{wrapper: css.defaultMargin}}
        >
          {t('Cancel', 'Cancel')}
        </Button>
        <Button
          type="primary"
          onClick={() => {
            onSubmit();
            const communicationModeQuestion = questions.find(
              (question) => question.response_field === 'communication_mode',
            );
            const communicationPreference =
              (communicationModeQuestion &&
                answers.get(communicationModeQuestion.id)) ??
              null;
            AnalyticsService.track('Form Submitted', {
              communicationPreference,
            });
          }}
          disabled={submitting}
          style={{
            backgroundColor: brandButtonColor,
            boxShadow: `0 0 0 2px ${brandButtonColor}`,
            color: DEFAULT_TEXT_COLOR,
          }}
        >
          {token
            ? t('Accept_Submit', 'Accept & Submit')
            : t('Submit', 'Submit')}
        </Button>
      </div>
      <TrackFormModal
        isOpen={showTrackModal}
        onClose={() => setShowTrackModal(false)}
      />
    </div>
  );
};

export default Form;
